import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  applyActionCode,
  verifyPasswordResetCode,
  confirmPasswordReset,
} from "firebase/auth";
import { auth } from "../firebase";

/**
 * Custom Firebase auth action handler.
 *
 * Firebase's default action links point at edora-dc48e.firebaseapp.com, which
 * looks untrustworthy in emails. In Firebase Console → Authentication →
 * Templates → "Customize action URL" this page is registered as the handler,
 * so links become https://www.profioplatform.com/auth/action?mode=...
 *
 * Handles: verifyEmail, resetPassword (recoverEmail falls back to a generic
 * apply attempt).
 */
export default function AuthAction() {
  const { t } = useTranslation();
  const [params] = useSearchParams();

  const mode = params.get("mode");
  const oobCode = params.get("oobCode");
  const continueUrl = params.get("continueUrl");

  // "working" | "verified" | "reset-form" | "reset-done" | "error"
  const [state, setState] = useState("working");
  const [errorMsg, setErrorMsg] = useState("");
  const [resetEmail, setResetEmail] = useState("");
  const [pw, setPw] = useState({ next: "", confirm: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!oobCode) {
        setState("error");
        setErrorMsg(t("authAction.invalidLink"));
        return;
      }
      try {
        if (mode === "resetPassword") {
          const email = await verifyPasswordResetCode(auth, oobCode);
          if (!alive) return;
          setResetEmail(email);
          setState("reset-form");
        } else {
          // verifyEmail, recoverEmail, revertSecondFactorAddition
          await applyActionCode(auth, oobCode);
          if (!alive) return;
          // Refresh the local user so emailVerified flips without re-login
          try { await auth.currentUser?.reload(); } catch {}
          setState("verified");
        }
      } catch (e) {
        if (!alive) return;
        setState("error");
        setErrorMsg(
          e?.code === "auth/expired-action-code" || e?.code === "auth/invalid-action-code"
            ? t("authAction.expiredLink")
            : e?.message || t("authAction.genericError")
        );
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, oobCode]);

  const submitNewPassword = async (e) => {
    e.preventDefault();
    if (pw.next.length < 6) {
      setErrorMsg(t("authAction.weakPassword"));
      return;
    }
    if (pw.next !== pw.confirm) {
      setErrorMsg(t("authAction.passwordMismatch"));
      return;
    }
    setBusy(true);
    setErrorMsg("");
    try {
      await confirmPasswordReset(auth, oobCode, pw.next);
      setState("reset-done");
    } catch (e2) {
      setErrorMsg(e2?.message || t("authAction.genericError"));
    } finally {
      setBusy(false);
    }
  };

  const continueTo = (() => {
    try {
      if (continueUrl) {
        const u = new URL(continueUrl);
        if (u.hostname.endsWith("profioplatform.com")) return u.pathname + u.search;
      }
    } catch {}
    return "/dashboard";
  })();

  const inputCls =
    "w-full rounded-xl bg-white/10 px-4 py-3 text-white placeholder-white/40 ring-1 ring-white/15 focus:outline-none focus:ring-[#7269e3]";

  return (
    <main className="min-h-screen bg-[#272c39] text-white">
      <div className="mx-auto flex max-w-md flex-col items-center px-4 pt-20 pb-24 text-center">
        {state === "working" && (
          <>
            <div className="h-10 w-10 animate-spin rounded-full border-2 border-white/20 border-t-[#7269e3]" />
            <p className="mt-6 text-white/70">{t("authAction.working")}</p>
          </>
        )}

        {state === "verified" && (
          <>
            <div className="grid h-16 w-16 place-content-center rounded-full bg-[#98dea3]/15 text-3xl">✅</div>
            <h1 className="mt-6 font-unbounded text-2xl font-bold">{t("authAction.verifiedTitle")}</h1>
            <p className="mt-3 text-white/70">{t("authAction.verifiedText")}</p>
            <Link
              to={continueTo}
              className="mt-8 rounded-xl bg-[#7269e3] px-6 py-3 font-semibold hover:brightness-110"
            >
              {t("authAction.continueBtn")}
            </Link>
          </>
        )}

        {state === "reset-form" && (
          <>
            <h1 className="font-unbounded text-2xl font-bold">{t("authAction.resetTitle")}</h1>
            <p className="mt-2 text-sm text-white/60">{resetEmail}</p>
            <form onSubmit={submitNewPassword} className="mt-8 w-full space-y-4 text-left">
              <input
                type="password"
                className={inputCls}
                placeholder={t("authAction.newPassword")}
                value={pw.next}
                onChange={(e) => setPw((p) => ({ ...p, next: e.target.value }))}
                required
              />
              <input
                type="password"
                className={inputCls}
                placeholder={t("authAction.confirmPassword")}
                value={pw.confirm}
                onChange={(e) => setPw((p) => ({ ...p, confirm: e.target.value }))}
                required
              />
              {errorMsg && <p className="text-sm text-red-300">{errorMsg}</p>}
              <button
                type="submit"
                disabled={busy}
                className="w-full rounded-xl bg-[#7269e3] px-4 py-3 font-semibold hover:brightness-110 disabled:opacity-60"
              >
                {t("authAction.resetBtn")}
              </button>
            </form>
          </>
        )}

        {state === "reset-done" && (
          <>
            <div className="grid h-16 w-16 place-content-center rounded-full bg-[#98dea3]/15 text-3xl">✅</div>
            <h1 className="mt-6 font-unbounded text-2xl font-bold">{t("authAction.resetDoneTitle")}</h1>
            <Link
              to="/login"
              className="mt-8 rounded-xl bg-[#7269e3] px-6 py-3 font-semibold hover:brightness-110"
            >
              {t("authAction.signInBtn")}
            </Link>
          </>
        )}

        {state === "error" && (
          <>
            <div className="grid h-16 w-16 place-content-center rounded-full bg-red-500/15 text-3xl">⚠️</div>
            <h1 className="mt-6 font-unbounded text-2xl font-bold">{t("authAction.errorTitle")}</h1>
            <p className="mt-3 text-white/70">{errorMsg}</p>
            <Link
              to="/login"
              className="mt-8 rounded-xl border border-white/20 px-6 py-3 font-semibold hover:bg-white/5"
            >
              {t("authAction.signInBtn")}
            </Link>
          </>
        )}
      </div>
    </main>
  );
}
